import React from 'react';
import PropTypes from 'prop-types';
import { VaRadio } from '@department-of-veterans-affairs/component-library/dist/react-bindings';
import { formatAddress } from './formatAddress';
import { USER_ENTERED_VALUE, suggestionValue } from './constants';

const addressPropType = PropTypes.shape({
  street: PropTypes.string,
  street2: PropTypes.string,
  city: PropTypes.string,
  state: PropTypes.string,
  postalCode: PropTypes.string,
  country: PropTypes.string,
});

/**
 * Radio tiles letting the user choose between the address they entered and
 * one or more USPS-suggested addresses. Layout follows the design-system
 * direction for address confirmation: warning alert, "which of these"
 * question, entered address first, suggestions after, nothing pre-selected —
 * matching the phone-number selection tiles.
 */
export default function SuggestedAddressRadio({
  title,
  headerLevel,
  userAddress,
  suggestions,
  selectedValue,
  error,
  onChangeSelectedAddress,
}) {
  const Title = headerLevel;
  return (
    <div>
      <Title>{title}</Title>
      <va-alert class="vads-u-margin-bottom--2" status="warning" visible slim>
        <p className="vads-u-margin-y--0">
          We can’t confirm the address you entered with the U.S. Postal Service.
        </p>
      </va-alert>
      <VaRadio
        label="Which of these addresses would you like to use?"
        onVaValueChange={onChangeSelectedAddress}
        error={error}
        required
      >
        {userAddress && (
          <va-radio-option
            key={USER_ENTERED_VALUE}
            name="addressGroup"
            label="Address you entered"
            description={formatAddress(userAddress)}
            value={USER_ENTERED_VALUE}
            tile
            checked={selectedValue === USER_ENTERED_VALUE}
          />
        )}
        {suggestions.map((suggestion, index) => (
          <va-radio-option
            key={suggestionValue(index)}
            name="addressGroup"
            label={
              suggestions.length > 1
                ? `Suggested address ${index + 1}`
                : 'Suggested address'
            }
            description={formatAddress(suggestion.formAddress)}
            value={suggestionValue(index)}
            tile
            checked={selectedValue === suggestionValue(index)}
          />
        ))}
      </VaRadio>
    </div>
  );
}

SuggestedAddressRadio.propTypes = {
  suggestions: PropTypes.arrayOf(
    PropTypes.shape({ formAddress: addressPropType }),
  ).isRequired,
  title: PropTypes.string.isRequired,
  onChangeSelectedAddress: PropTypes.func.isRequired,
  error: PropTypes.string,
  headerLevel: PropTypes.oneOf(['h1', 'h2', 'h3', 'h4']),
  selectedValue: PropTypes.string,
  userAddress: addressPropType,
};

SuggestedAddressRadio.defaultProps = {
  error: null,
  headerLevel: 'h3',
  selectedValue: null,
  userAddress: null,
};
